/**
 * AuthProvider — Phase 22 (mayo 2026)
 * -----------------------------------
 * La sesión vive en una cookie httpOnly `cp_session` emitida por el backend.
 * Este provider únicamente mantiene el OBJETO USUARIO en estado React + un
 * caché en localStorage para evitar parpadeos al recargar (`cp_user`).
 *
 * Flujo:
 *  - Al montar: si hay caché de usuario, validamos contra `/api/auth/me`
 *    (con la cookie). Si la cookie está caducada → limpiamos caché y dejamos
 *    sin usuario. Si no había caché, igualmente intentamos `/me` por si el
 *    navegador conserva la cookie de una sesión previa.
 *  - login(): POST → el backend setea la cookie + devuelve user en JSON.
 *  - logout(): POST `/api/auth/logout` para que el backend invalide la cookie
 *    + limpiamos el caché de usuario en cliente.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api, getStoredUser, setStoredUser, clearClientAuthCache, esRutaDePaciente } from './api';
import { olvidarBorradoresDelChat } from './chatSinLeer';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getStoredUser());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Phase 22: en lugar de mirar localStorage para un token, simplemente
    // intentamos /api/auth/me. La cookie (si existe y es válida) revalidará
    // la sesión; en caso contrario el interceptor global resetea estado.
    let cancelled = false;
    if (esRutaDePaciente()) {
      // Página pública del paciente: no hay sesión que revalidar.
      setLoading(false);
      return undefined;
    }
    api.get('/api/auth/me')
      .then((res) => {
        if (cancelled) return;
        setUser(res.data);
        setStoredUser(res.data);
      })
      .catch(() => {
        if (cancelled) return;
        // No log: 401 es la respuesta esperada si no hay sesión activa.
        clearClientAuthCache();
        setUser(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Latido de sesión (2026-09-02): la cookie dura 24 h y el backend la renueva
  // (sliding) en cualquier petición cuando quedan <12 h. Una pestaña abierta
  // horas sin tocar nada (una hoja de anestesia a medias) no hacía ninguna
  // petición y caducaba a las 24 h exactas del login, perdiendo lo escrito.
  // Cada 10 min, y al volver a la pestaña, se pide /api/auth/me: si la
  // sesión sigue viva se renueva sola; si ya caducó, el interceptor global
  // hace lo de siempre.
  useEffect(() => {
    if (!user) return undefined;
    const latido = () => {
      if (document.visibilityState === 'hidden') return;
      api.get('/api/auth/me').catch(() => {});
    };
    const id = setInterval(latido, 10 * 60 * 1000);
    document.addEventListener('visibilitychange', latido);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', latido);
    };
  }, [user]);

  const login = async (email, password) => {
    setError(null);
    try {
      // El backend setea la cookie httpOnly; el access_token JSON se ignora
      // del lado del cliente (sólo se conserva por back-compat para tests).
      const res = await api.post('/api/auth/login', { email, password });
      const { user: u } = res.data;
      setStoredUser(u);
      setUser(u);
      return { ok: true };
    } catch (e) {
      // Mejor UX: distinguir 401 (credenciales) de errores de red genuinos.
      // Si el backend respondió cualquier 4xx/5xx → mostramos su detail.
      // Si no hay respuesta (timeout/DNS/CORS) → mostramos un texto explícito
      // en lugar del genérico "Network Error" de axios.
      let msg;
      if (e?.response?.data?.detail) {
        msg = e.response.data.detail;
      } else if (e?.response?.status === 401) {
        msg = 'Usuario o contraseña incorrectos';
      } else if (e?.response) {
        msg = `Error del servidor (${e.response.status})`;
      } else if (e?.code === 'ECONNABORTED') {
        msg = 'El servidor tardó demasiado en responder. Reintenta.';
      } else {
        msg = 'No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.';
      }
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const logout = async () => {
    // Pedimos al backend que invalide la cookie. Si falla (offline o
    // servidor caído) limpiamos el caché igualmente para no dejar al
    // usuario "atrapado" en una UI que cree que está logueado.
    try {
      await api.post('/api/auth/logout');
    } catch (err) {
      // No es crítico: el cliente queda limpio aunque el backend no responda.
      // Log informativo (no toast) para no asustar al usuario; debugging y
      // observabilidad sí lo necesitan.
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[auth] logout: backend no respondió, limpiando cliente:', err?.message || err);
      }
    }
    clearClientAuthCache();
    // Lo que se estaba escribiendo en el chat no se queda para el siguiente
    // que use este navegador (23/9/2026).
    olvidarBorradoresDelChat();
    setUser(null);
  };

  const updateUser = (patch) => {
    setUser((prev) => {
      const next = prev ? { ...prev, ...patch } : prev;
      if (next) setStoredUser(next);
      return next;
    });
  };

  // Code Quality Phase 23.5 — el objeto del context se memoiza para evitar
  // que TODOS los consumidores de useAuth() re-rendericen cuando el
  // AuthProvider re-renderiza por motivos ajenos a los valores expuestos.
  // Sigue siendo un objeto fresco cuando cualquiera de las dependencias cambia,
  // lo cual es el comportamiento correcto.
  const value = useMemo(
    () => ({ user, loading, error, login, logout, updateUser }),
    // login/logout/updateUser son funciones declaradas en cada render pero el
    // contexto solo necesita re-emitir cuando user/loading/error cambian. Si
    // luego envolvemos esas funciones en useCallback, las añadiríamos aquí.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, error],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
